import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLanguage } from '../LanguageContext';
import { useBrand } from '../BrandContext';
import { useAuth } from '../AuthContext';
import DealRegisterModal from '../components/UnitsDeals/DealRegisterModal';
import { fmt, projLabel } from '../components/UnitsDeals/constants';
import {
  dealRequestFormData,
  listDealRequests,
  submitDealRequest,
  approveDealRequest,
  rejectDealRequest,
} from '../api';

const STATUS = {
  pending: { ar: 'قيد المراجعة', en: 'Pending', color: '#f59e0b' },
  approved: { ar: 'معتمدة', en: 'Approved', color: '#22c55e' },
  rejected: { ar: 'مرفوضة', en: 'Rejected', color: '#ef4444' },
};

// ── DealRequests ──
// Sales reps submit a deal (same DealRegisterModal as admin) which lands here as
// a pending request. Admin/manager approve (materializes the real deal + reserves
// the unit) or reject with a reason. Sales see only their own submissions.
export default function DealRequests() {
  const { t, lang } = useLanguage();
  const brand = useBrand();
  const { user, can } = useAuth();
  const ar = lang === 'ar';

  const canSubmit = can('deals.request');
  const canApprove = can('deals.approve');

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ units: [], brokers: [], team: [] });
  const [modalOpen, setModalOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('all');
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(() => {
    return listDealRequests().then((r) => { setRequests(r.requests || []); setLoading(false); });
  }, []);

  useEffect(() => {
    load();
    dealRequestFormData().then(setFormData);
  }, [load]);

  const flash = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const counts = useMemo(() => {
    const c = { all: requests.length, pending: 0, approved: 0, rejected: 0 };
    requests.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [requests]);

  const visible = useMemo(
    () => (filter === 'all' ? requests : requests.filter((r) => r.status === filter)),
    [requests, filter]
  );

  const submitErr = (code) => {
    const m = {
      unit_not_available: ar ? 'الوحدة لم تعد متاحة' : 'Unit is no longer available',
      unit_not_found: ar ? 'الوحدة غير موجودة' : 'Unit not found',
      request_already_pending: ar ? 'يوجد طلب قيد المراجعة لهذه الوحدة بالفعل' : 'A pending request already exists for this unit',
      price_required: ar ? 'السعر مطلوب' : 'Price is required',
      client_name_required: ar ? 'اسم العميل مطلوب' : 'Customer name is required',
    };
    return m[code] || (ar ? 'تعذّر إرسال الطلب' : 'Could not submit the request');
  };

  const handleSubmit = async (payload) => {
    const res = await submitDealRequest(payload);
    if (res?.error) { flash('error', submitErr(res.error)); return; }
    setModalOpen(false);
    flash('success', ar ? 'تم إرسال الطلب للمراجعة' : 'Request submitted for review');
    load();
  };

  const handleApprove = async (id) => {
    setBusyId(id);
    const res = await approveDealRequest(id);
    setBusyId(null);
    if (res?.error) {
      flash('error', res.error === 'unit_no_longer_available'
        ? (ar ? 'الوحدة لم تعد متاحة' : 'Unit is no longer available')
        : (ar ? 'تعذّر اعتماد الطلب' : 'Could not approve'));
      load();
      return;
    }
    flash('success', ar ? 'تم اعتماد الصفقة' : 'Deal approved');
    load();
  };

  const confirmReject = async () => {
    const id = rejectId;
    const reason = rejectReason.trim();
    setRejectId(null);
    setRejectReason('');
    setBusyId(id);
    const res = await rejectDealRequest(id, reason);
    setBusyId(null);
    if (res?.error) { flash('error', ar ? 'تعذّر رفض الطلب' : 'Could not reject'); return; }
    flash('success', ar ? 'تم رفض الطلب' : 'Request rejected');
    load();
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString(ar ? 'ar-SA' : 'en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
    catch { return '—'; }
  };

  const TABS = [
    { key: 'all', label: ar ? 'الكل' : 'All', color: 'var(--accent)' },
    { key: 'pending', label: ar ? STATUS.pending.ar : STATUS.pending.en, color: STATUS.pending.color },
    { key: 'approved', label: ar ? STATUS.approved.ar : STATUS.approved.en, color: STATUS.approved.color },
    { key: 'rejected', label: ar ? STATUS.rejected.ar : STATUS.rejected.en, color: STATUS.rejected.color },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, margin: 0 }}>
            {ar ? 'طلبات الصفقات' : 'Deal Requests'}
          </h1>
          <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: 4 }}>
            {canApprove
              ? (ar ? 'راجع الصفقات المُرسلة من فريق المبيعات واعتمدها أو ارفضها.' : 'Review deals submitted by the sales team and approve or reject them.')
              : (ar ? 'سجّل صفقة جديدة لاعتمادها من الإدارة.' : 'Submit a new deal for management approval.')}
          </div>
        </div>
        {canSubmit && (
          <button className="btn btn-primary" onClick={() => setModalOpen(true)} style={{ padding: '10px 18px', fontWeight: 800, whiteSpace: 'nowrap' }}>
            {ar ? '＋ تسجيل صفقة' : '＋ Register Deal'}
          </button>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          marginBottom: 12, padding: '10px 14px', borderRadius: 10, fontWeight: 700, fontSize: '0.86rem',
          display: 'flex', alignItems: 'center', gap: 8,
          background: (toast.type === 'success' ? '#22c55e' : '#ef4444') + '14',
          color: toast.type === 'success' ? '#16a34a' : '#ef4444',
          border: '1px solid ' + (toast.type === 'success' ? '#22c55e' : '#ef4444') + '40',
        }}>
          <span>{toast.type === 'success' ? '✓' : '⚠'}</span>{toast.text}
        </div>
      )}

      {/* Filter tabs (double as a status summary with counts) */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {TABS.map((tb) => {
          const active = filter === tb.key;
          return (
            <button
              key={tb.key}
              onClick={() => setFilter(tb.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 20,
                border: active ? `1.5px solid ${tb.color}` : '1px solid var(--border)',
                background: active ? tb.color + '14' : 'var(--bg-card)',
                color: active ? tb.color : 'var(--text-secondary)', cursor: 'pointer', fontWeight: 800, fontSize: '0.82rem',
              }}
            >
              <span>{tb.label}</span>
              <span style={{
                background: active ? tb.color : 'var(--border)', color: active ? '#fff' : 'var(--text-secondary)',
                borderRadius: 10, padding: '0 7px', fontSize: '0.7rem', fontWeight: 800, minWidth: 20, textAlign: 'center',
              }}>{counts[tb.key]}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '1.8rem', marginBottom: 8, opacity: 0.4 }}>&#9203;</div>
          {ar ? 'جارٍ التحميل…' : 'Loading…'}
        </div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, background: 'var(--bg-card)', border: '1px dashed var(--border)', borderRadius: 12, color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '2rem', opacity: 0.35, marginBottom: 8 }}>&#128203;</div>
          <div style={{ fontWeight: 700 }}>
            {filter === 'pending' ? (ar ? 'لا توجد طلبات قيد المراجعة' : 'No pending requests')
              : filter === 'approved' ? (ar ? 'لا توجد طلبات معتمدة' : 'No approved requests')
              : filter === 'rejected' ? (ar ? 'لا توجد طلبات مرفوضة' : 'No rejected requests')
              : (ar ? 'لا توجد طلبات بعد' : 'No requests yet')}
          </div>
          {canSubmit && filter !== 'rejected' && filter !== 'approved' && (
            <div style={{ fontSize: '0.84rem', marginTop: 6 }}>{ar ? 'اضغط «تسجيل صفقة» لإضافة طلب.' : 'Use “Register Deal” to add one.'}</div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visible.map((r) => {
            const p = r.payload || {};
            const st = STATUS[r.status] || { ar: r.status, en: r.status, color: 'var(--text-secondary)' };
            const busy = busyId === r.id;
            return (
              <div key={r.id} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderInlineStart: `4px solid ${st.color}`, borderRadius: 12, padding: '14px 16px',
              }}>
                {/* Top row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: '1.02rem', color: 'var(--text-primary)' }}>
                      {projLabel(r.project, t)} · {r.unit_code}
                    </div>
                    <div style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                      {p.client_name || '—'}
                      {p.client_phone ? <span style={{ direction: 'ltr' }}> · {p.client_phone}</span> : null}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: ar ? 'flex-start' : 'flex-end', gap: 5 }}>
                    <span style={{ padding: '4px 11px', borderRadius: 12, fontSize: '0.76rem', fontWeight: 800, background: st.color + '18', color: st.color, whiteSpace: 'nowrap' }}>
                      {ar ? st.ar : st.en}
                    </span>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                      {r.submitted_by} · {fmtDate(r.created_at)}
                    </div>
                  </div>
                </div>

                {/* Meta grid */}
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 10,
                  marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--border)',
                }}>
                  <Meta label={ar ? 'السعر' : 'Price'} value={fmt(p.actual_price || p.price || 0)} strong ltr />
                  {p.closer ? <Meta label={ar ? 'المنفّذ' : 'Closer'} value={p.closer} /> : null}
                  {p.earnest_money ? <Meta label={ar ? 'العربون' : 'Earnest'} value={fmt(p.earnest_money)} ltr /> : null}
                  {p.payment_method ? <Meta label={ar ? 'طريقة الدفع' : 'Payment'} value={p.payment_method} /> : null}
                  {p.broker_name ? <Meta label={ar ? 'الوسيط' : 'Broker'} value={p.broker_name} /> : null}
                </div>

                {/* Rejected reason */}
                {r.status === 'rejected' && r.reject_reason && (
                  <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: '#ef444412', border: '1px solid #ef444433', fontSize: '0.82rem', color: '#b91c1c' }}>
                    <b>{ar ? 'سبب الرفض: ' : 'Reason: '}</b>{r.reject_reason}
                  </div>
                )}
                {r.status !== 'pending' && r.reviewed_by && (
                  <div style={{ marginTop: 8, fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                    {ar ? 'تمت المراجعة بواسطة ' : 'Reviewed by '}<b>{r.reviewed_by}</b>
                  </div>
                )}

                {/* Approver actions */}
                {canApprove && r.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <button
                      disabled={busy}
                      onClick={() => handleApprove(r.id)}
                      style={{ flex: 1, padding: '10px 16px', fontWeight: 800, fontSize: '0.88rem', borderRadius: 8, border: 'none', cursor: 'pointer', background: '#22c55e', color: '#fff', opacity: busy ? 0.6 : 1 }}
                    >
                      {busy ? '…' : (ar ? '✓ اعتماد' : '✓ Approve')}
                    </button>
                    <button
                      disabled={busy}
                      onClick={() => { setRejectId(r.id); setRejectReason(''); }}
                      style={{ flex: 1, padding: '10px 16px', fontWeight: 800, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer', background: 'transparent', color: '#ef4444', border: '1.5px solid #ef444455', opacity: busy ? 0.6 : 1 }}
                    >
                      {ar ? '✕ رفض' : '✕ Reject'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Register-deal modal */}
      {modalOpen && (
        <DealRegisterModal
          unit={null}
          units={formData.units}
          brandProjects={brand.projects || []}
          brokers={formData.brokers}
          team={formData.team}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
          onAddBroker={() => {}}
          forcedCloser={user?.role === 'sales' ? user?.name : null}
          t={t}
          lang={lang}
        />
      )}

      {/* Reject dialog */}
      {rejectId && (
        <div
          onClick={() => setRejectId(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 20, width: '100%', maxWidth: 420, border: '1px solid var(--border)' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: '1.1rem', fontWeight: 800, color: '#ef4444' }}>{ar ? 'رفض الطلب' : 'Reject request'}</h3>
            <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {ar ? 'اكتب سبب الرفض ليظهر لموظف المبيعات (اختياري).' : 'Add a reason shown to the rep (optional).'}
            </p>
            <textarea
              autoFocus
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder={ar ? 'سبب الرفض…' : 'Rejection reason…'}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', fontSize: '0.9rem', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button onClick={() => setRejectId(null)} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontWeight: 700, cursor: 'pointer' }}>
                {ar ? 'إلغاء' : 'Cancel'}
              </button>
              <button onClick={confirmReject} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>
                {ar ? 'تأكيد الرفض' : 'Confirm reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// One labelled metric inside a request card.
function Meta({ label, value, strong, ltr }) {
  if (value == null || value === '') return null;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-secondary)', fontWeight: 700, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: strong ? '0.98rem' : '0.84rem', fontWeight: strong ? 800 : 600, color: strong ? 'var(--accent)' : 'var(--text-primary)', direction: ltr ? 'ltr' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}
